import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  useTheme,
} from '@mui/material';
import { HourglassEmpty } from '@mui/icons-material';

const PendingApproval = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: isDarkMode ? '#0d1117' : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: isDarkMode ? `
            radial-gradient(circle at 40% 60%, rgba(235, 203, 139, 0.1) 0%, transparent 40%),
            radial-gradient(circle at 60% 40%, rgba(136, 192, 208, 0.1) 0%, transparent 40%)
          ` : `
            radial-gradient(circle at 40% 60%, rgba(235, 203, 139, 0.2) 0%, transparent 40%),
            radial-gradient(circle at 60% 40%, rgba(136, 192, 208, 0.2) 0%, transparent 40%)
          `,
          animation: 'aurora 20s ease-in-out infinite',
          '@keyframes aurora': {
            '0%, 100%': { transform: 'translate(0, 0)' },
            '50%': { transform: 'translate(2%, 2%)' },
          },
        },
      }}
    >
      {/* Floating orb */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '20%',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: isDarkMode 
            ? 'radial-gradient(circle, rgba(235, 203, 139, 0.08) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(235, 203, 139, 0.15) 0%, transparent 70%)',
          filter: 'blur(40px)',
          animation: 'float 10s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-30px)' },
          },
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Card
          sx={{
            background: isDarkMode 
              ? 'rgba(22, 27, 34, 0.7)'
              : 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
            borderRadius: 4,
            textAlign: 'center',
            boxShadow: isDarkMode ? `
              0 32px 64px rgba(0, 0, 0, 0.4),
              0 0 80px rgba(235, 203, 139, 0.1)
            ` : `
              0 32px 64px rgba(0, 0, 0, 0.12),
              0 0 80px rgba(235, 203, 139, 0.08)
            `,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background: 'linear-gradient(90deg, transparent, #ebcb8b, #88c0d0, transparent)',
              borderRadius: '4px 4px 0 0',
            },
          }}
        >
          <CardContent sx={{ p: 5 }}>
            {/* Animated hourglass icon */}
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: isDarkMode 
                  ? 'linear-gradient(135deg, rgba(235, 203, 139, 0.2) 0%, rgba(136, 192, 208, 0.2) 100%)'
                  : 'linear-gradient(135deg, rgba(235, 203, 139, 0.3) 0%, rgba(136, 192, 208, 0.3) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { 
                    transform: 'scale(1)',
                    boxShadow: '0 0 0 0 rgba(235, 203, 139, 0.4)',
                  },
                  '50%': { 
                    transform: 'scale(1.05)',
                    boxShadow: '0 0 40px 10px rgba(235, 203, 139, 0.2)',
                  },
                },
              }}
            >
              <HourglassEmpty
                sx={{
                  fontSize: 50,
                  color: '#ebcb8b',
                  animation: 'rotate 3s ease-in-out infinite',
                  '@keyframes rotate': {
                    '0%, 100%': { transform: 'rotate(0deg)' },
                    '50%': { transform: 'rotate(180deg)' },
                  },
                }}
              />
            </Box>

            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #ebcb8b 0%, #88c0d0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 3,
              }}
            >
              Ожидание одобрения
            </Typography>

            <Typography variant="body1" sx={{ color: 'text.primary', mb: 2 }}>
              ✅ Ваша регистрация успешно завершена!
            </Typography>

            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
              Ваш аккаунт находится на рассмотрении у администратора. После одобрения вы
              получите возможность войти в систему.
            </Typography>

            <Box
              sx={{
                p: 2,
                background: isDarkMode ? 'rgba(136, 192, 208, 0.1)' : 'rgba(136, 192, 208, 0.15)',
                border: '1px solid rgba(136, 192, 208, 0.2)',
                borderRadius: 2,
                mb: 4,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#88c0d0', fontStyle: 'italic' }}
              >
                ⏱️ Обычно проверка занимает от нескольких минут до нескольких часов.
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                boxShadow: '0 8px 32px rgba(136, 192, 208, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5e81ac 0%, #b48ead 100%)',
                  boxShadow: '0 12px 40px rgba(136, 192, 208, 0.4)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Вернуться на страницу входа
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            textAlign: 'center', 
            mt: 4, 
            color: 'text.secondary',
          }}
        >
          © 2024 Nord CRM
        </Typography>
      </Container>
    </Box>
  );
};

export default PendingApproval;
